
import React, { useState, useEffect } from "react";
import { FormControl, TextField, Box, Grid2, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LockIcon from '@mui/icons-material/Lock';

export default function ConnectionEmailForm({ connection, onDataChange, validationError, setValidationError, editMode }) {
    const [data, setData] = useState(JSON.parse(connection.data || '{"enableSSL": true}'));
    const [loadingEncrypt, setLoadingEncrypt] = useState(false);
    const [encryptedFields, setEncryptedFields] = useState([]);
    const id = connection.id || "new";

    async function encryptValue(value) {
        try {
            setLoadingEncrypt(true);
            const response = await fetch('/api/encryption/encrypt', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: value }),
            });
            if (response.ok) {
                const result = await response.json();
                return result.encryptedText;
            } else {
                console.error('Encryption failed');
                return value; // Return original value if encryption fails
            }
        } catch (error) {
            console.error('Error during encryption:', error);
            return value; // Return original value on error
        } finally {
            setLoadingEncrypt(false);
        }
    }

    async function decryptValue(value) {
        try {
            setLoadingEncrypt(true);
            const response = await fetch('/api/encryption/decrypt', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: value }),
            });
            if (response.ok) {
                const result = await response.json();
                return result.decryptedText;
            } else {
                console.error('Decryption failed');
                return value; // Return original value if decryption fails
            }
        } catch (error) {
            console.error('Error during decryption:', error);
            return value; // Return original value on error
        } finally {
            setLoadingEncrypt(false);
        }
    }

    useEffect(() => {
        let newData = JSON.parse(connection.data || '{}');
        setEncryptedFields(newData.decrypt || []);
    }, [connection.data]);

    const handleDataChange = async (key, value) => {
        let newData = { ...data, [key]: key == 'port' ? +value : value };
        setData(newData);
        onDataChange(newData);
    }

    const handleEncryptCheckboxChange = async (variable, encrypt) => {
        let val = data[variable] || '';
        let newEncryptedFields = [...encryptedFields];
        if (encrypt) {
            const valueToEncrypt = data[variable] || '';
            val = await encryptValue(valueToEncrypt);
            newEncryptedFields = [...encryptedFields, variable];
        }
        else {
            val = await decryptValue(data[variable] || '');
            newEncryptedFields = encryptedFields.filter(field => field !== variable);
        }
        setEncryptedFields(newEncryptedFields);
        let newData = { ...data, [variable]: val, decrypt: newEncryptedFields };
        setData(newData);
        onDataChange(newData);
    }

    return (
        <div>
            <h4>Database Connection Details</h4>
            <Grid2 container columns={16} spacing={1}>
                <Grid2 size={14}>
                </Grid2>
                <Grid2 size={1}>
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <LockIcon />
                    </Box>
                </Grid2>
            </Grid2>
            <FormControlLabel
                control={
                    <Checkbox checked={data.enableSSL ?? true} onChange={(event) => handleDataChange("enableSSL", event.target.checked)} name="Enable SSL" />
                }
                label="Enable SSL"
            />
            <Grid2 container columns={16} spacing={1}>
                <Grid2 size={14}>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="Host"
                            id={`host-${id}`}
                            name={`host-${id}`}
                            type="text"
                            value={data.host || ''}
                            onChange={(event) => handleDataChange('host', event.target.value)}
                            InputProps={{
                                readOnly: !editMode
                            }}
                            variant={editMode ? "outlined" : "standard"}
                        />
                    </FormControl>
                </Grid2>
            </Grid2>
            <Grid2 container columns={16} spacing={1}>
                <Grid2 size={14}>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="port"
                            id={`port-${id}`}
                            name={`port-${id}`}
                            type="number"
                            value={data.port || ''}
                            onChange={(event) => handleDataChange('port', event.target.value)}
                            InputProps={{
                                readOnly: !editMode
                            }}
                            variant={editMode ? "outlined" : "standard"}
                        />
                    </FormControl>
                </Grid2>
            </Grid2>
            <Grid2 container columns={16} spacing={1}>
                <Grid2 size={14}>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            disabled={encryptedFields.includes('email')}
                            label="Email"
                            id={`email-${id}`}
                            name={`email-${id}`}
                            type="text"
                            value={data.email || ''}
                            onChange={(event) => handleDataChange('email', event.target.value)}
                            InputProps={{
                                readOnly: !editMode || encryptedFields.includes('email')
                            }}
                            variant={editMode && !encryptedFields.includes('email') ? "outlined" : "standard"}
                        />
                    </FormControl>
                </Grid2>
                <Grid2 size={1}>
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <IconButton variant="outlined" color="primary" size="small" disabled={!editMode}>
                            {encryptedFields.includes('email') ? (
                                <CheckBoxIcon onClick={() => handleEncryptCheckboxChange('email', false)} />
                            ) : (
                                <CheckBoxOutlineBlankIcon onClick={() => handleEncryptCheckboxChange('email', true)} />
                            )}
                        </IconButton>
                    </Box>
                </Grid2>
            </Grid2>
            <Grid2 container columns={16} spacing={1}>
                <Grid2 size={14}>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            disabled={encryptedFields.includes('password')}
                            label="Password"
                            id={`password-${id}`}
                            name={`password-${id}`}
                            type="text"
                            value={data.password || ''}
                            onChange={(event) => handleDataChange('password', event.target.value)}
                            InputProps={{
                                readOnly: !editMode || encryptedFields.includes('password')
                            }}
                            variant={editMode && !encryptedFields.includes('password') ? "outlined" : "standard"}
                        />
                    </FormControl>
                </Grid2>
                <Grid2 size={1}>
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <IconButton variant="outlined" color="primary" size="small" disabled={!editMode || loadingEncrypt}>
                            {encryptedFields.includes('password') ? (
                                <CheckBoxIcon onClick={() => handleEncryptCheckboxChange('password', false)} />
                            ) : (
                                <CheckBoxOutlineBlankIcon onClick={() => handleEncryptCheckboxChange('password', true)} />
                            )}
                        </IconButton>
                    </Box>
                </Grid2>
            </Grid2>
        </div>
    );
}
